import { graphql, readInlineData } from "react-relay";

import { article as articleData } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import type { helpersCheckIsLiveArticle$key } from "scmp-app/queries/__generated__/helpersCheckIsLiveArticle.graphql";

import { ItemNamePrefix } from "./consts";

type MaybeLiveContent = null | undefined | { customTime?: null | number };

// Shared ordering for every live-contents presentation (content, timeline,
// and the bot-only SSR preview): newest update first, descending customTime.
// The API default order is oldest-first, so keep all renderers on this single
// sort — otherwise the bot HTML and the hydrated timeline would disagree.
export const sortLiveContentsByCustomTime = <T extends MaybeLiveContent>(
  liveContents?: null | ReadonlyArray<null | T | undefined>,
) => {
  const items = liveContents?.filter(notEmpty) ?? [];
  return items.toSorted((a, b) => (b.customTime ?? 0) - (a.customTime ?? 0));
};

export const getTimelineItemClassName = (entityUuid?: null | string) => {
  if (!entityUuid) return;
  return `${ItemNamePrefix.Timeline}_${entityUuid}`;
};

export const getContentItemClassName = (entityUuid?: null | string) => {
  if (!entityUuid) return;
  return `${ItemNamePrefix.Content}_${entityUuid}`;
};

export const makeSureContentItemExists = (entityUuid: string, retry = 5, debounce = 1000) =>
  new Promise<void>(resolve => {
    const run = (currentTime: number, lastTime = 0, _retry = retry) => {
      if (_retry === 0) {
        resolve();
        return;
      }
      if (currentTime - lastTime < debounce) {
        setTimeout(() => {
          requestAnimationFrame(time => run(time, currentTime, _retry));
        }, debounce);
        return;
      }
      if (document.querySelector(`.${getContentItemClassName(entityUuid)}`) === null) {
        requestAnimationFrame(time => run(time, currentTime, _retry - 1));
        return;
      }

      resolve();
    };
    requestAnimationFrame(run);
  });

export const checkIsLiveArticle = (reference: helpersCheckIsLiveArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersCheckIsLiveArticle on Article @inline {
        flattenTypeIds
        liveArticle {
          status
        }
      }
    `,
    reference,
  );

  const isLiveArticle =
    article.flattenTypeIds?.includes(articleData.types.live.entityUuid) ?? false;
  const isLive = article.liveArticle?.status ?? false;

  return { isLive, isLiveArticle };
};
